import type { ReactNode } from 'react';
import { TaskHeader, TaskNotice, TaskSessionComplete, TaskShell, TaskStageSkeleton } from '@/features/contribution-kit';
import { fill } from '../copy';
import type { InitialDataCopy } from '../initial-data-copy';
import type { SavedSessionPosition } from '../session-position.storage';
import type { InitialDataCatalog, InitialDataLanguage, InitialDataMode, InitialDataStep } from '../types';
import {
  canStartSession,
  completeDescription,
  languageLabel,
  resolveInitialDataView,
  sectorName,
  setupStats,
  type RecordingControls,
} from '../initial-data.view';
import { InitialDataSession, type SessionView } from './InitialDataSession';
import { InitialDataSetup } from './InitialDataSetup';
import { InitialDataState } from './InitialDataState';
import { ResumeOffer } from './ResumeOffer';
import type { RecordingDeviceSelection } from '@/features/recording-device-metadata';

export type InitialDataWorkspaceProps = {
  copy: InitialDataCopy;
  mode: InitialDataMode;
  step: InitialDataStep;
  languages: InitialDataLanguage[];
  isLoadingLanguages: boolean;
  language: string;
  recordingDevice: RecordingDeviceSelection;
  languageName: string;
  sectorId: string;
  catalog: InitialDataCatalog;
  session: SessionView;
  isSubmitting: boolean;
  canSubmit: boolean;
  blocked: boolean;
  answerPanel: ReactNode;
  /** Spoken answers: record, stop, pause and redo in the bottom bar. */
  recording?: RecordingControls;
  /** Code of the community profile a collector answers for, if any. */
  collectorProfileCode?: string;
  /** Where the session is collected (spec 004); required before starting. */
  placeStep: ReactNode;
  isPlaceReady: boolean;
  /** Paused session of this mode, offered again on the start screen. */
  savedPosition: SavedSessionPosition | null;
  /** Locale for dates, e.g. 'fr'. */
  locale: string;
  onResume: () => void;
  onRestart: () => void;
  onPause: () => void;
  onLanguageChange: (code: string) => void;
  onRecordingDeviceChange: (device: RecordingDeviceSelection) => void;
  onSectorChange: (sectorId: string) => void;
  onStart: () => void;
  onSubmit: () => void;
  onChangeSector: () => void;
  onLeave: () => void;
};

/** Setup, session, end screen and plain states of an initial-data page. */
export function InitialDataWorkspace(props: InitialDataWorkspaceProps) {
  const { copy, catalog, session } = props;
  const selected = Boolean(props.language && props.sectorId);
  const task = props.mode === 'audio' ? 'answerAudio' : 'answerText';

  const collectorNotice = props.collectorProfileCode ? (
    <TaskNotice tone="info" icon="users" title={fill(copy.collectorNotice, { code: props.collectorProfileCode })}>
      {copy.collectorNoticeBody}
    </TaskNotice>
  ) : null;

  const view = resolveInitialDataView({
    step: props.step,
    isLoadingQuestions: catalog.isLoadingQuestions,
    questionsError: catalog.questionsError,
    questionCount: catalog.questions.length,
    isComplete: session.isComplete,
    hasCurrent: session.current !== null,
  });

  if (view === 'setup') {
    const saved = props.savedPosition;
    return (
      <InitialDataSetup
        copy={copy}
        mode={props.mode}
        languages={props.languages}
        isLoadingLanguages={props.isLoadingLanguages}
        language={props.language}
        recordingDevice={props.recordingDevice}
        sectors={catalog.sectors}
        isLoadingSectors={catalog.isLoadingSectors}
        sectorsError={catalog.sectorsError}
        sectorId={props.sectorId}
        stats={setupStats(copy, {
          selected,
          questionCount: selected ? catalog.questions.length : 0,
          answeredCount: catalog.submittedQuestionIds.size,
          mode: props.mode,
        })}
        canStart={canStartSession(props)}
        isPreparing={catalog.isLoadingQuestions}
        placeStep={props.placeStep}
        notices={
          collectorNotice || (saved && props.isPlaceReady) ? (
            <>
              {collectorNotice}
              {/* A resumed session is a session too: it needs its place first. */}
              {saved && props.isPlaceReady ? (
                <ResumeOffer
                  copy={copy}
                  sectorName={sectorName(catalog.sectors, saved.sectorId)}
                  languageName={languageLabel(props.languages, saved.language)}
                  answeredCount={saved.answeredCount}
                  totalCount={saved.totalCount}
                  skippedCount={saved.skippedQuestionIds.length}
                  pausedAt={saved.updatedAt}
                  locale={props.locale}
                  onResume={props.onResume}
                  resumeDisabled={props.mode === 'audio' && !canStartSession(props)}
                  onRestart={props.onRestart}
                />
              ) : null}
            </>
          ) : undefined
        }
        onLanguageChange={props.onLanguageChange}
        onRecordingDeviceChange={props.onRecordingDeviceChange}
        onSectorChange={props.onSectorChange}
        onRetry={catalog.retry}
        onBack={props.onLeave}
        onStart={props.onStart}
      />
    );
  }

  if (view === 'loading') {
    return (
      <div data-testid="initial-data-loading" aria-label={copy.questionsLoading}>
        <TaskStageSkeleton />
      </div>
    );
  }

  if (view === 'error' || view === 'empty') {
    return (
      <TaskShell header={<TaskHeader task={task} onBack={props.onChangeSector} />}>
        {view === 'error' ? (
          <InitialDataState
            testId="initial-data-error"
            icon="alertCircle"
            tone="danger"
            title={copy.loadErrorTitle}
            body={copy.loadErrorBody}
            actions={[
              { label: copy.retry, onClick: catalog.retry, primary: true },
              { label: copy.changeSector, onClick: props.onChangeSector },
            ]}
          />
        ) : (
          <InitialDataState
            testId="initial-data-empty"
            icon="inbox"
            title={copy.noQuestionsTitle}
            body={copy.noQuestionsBody}
            actions={[{ label: copy.changeSector, onClick: props.onChangeSector, primary: true }]}
          />
        )}
      </TaskShell>
    );
  }

  if (view === 'complete' || !session.current) {
    const skippedCount = session.tally.skipped;
    return (
      <div data-testid="initial-data-complete">
        <TaskSessionComplete
          title={copy.completeTitle}
          description={completeDescription(copy, session.submittedThisSession)}
          stats={[
            { label: copy.statSent, value: session.submittedThisSession, tone: session.submittedThisSession > 0 ? 'success' : 'default' },
            { label: copy.statSkipped, value: skippedCount },
          ]}
          continueLabel={`${copy.reviewSkipped} (${skippedCount})`}
          onContinue={skippedCount > 0 ? session.reviewSkipped : undefined}
          homeLabel={copy.changeSector}
          onHome={props.onChangeSector}
        />
      </div>
    );
  }

  return (
    <InitialDataSession
      copy={copy}
      mode={props.mode}
      question={session.current}
      questions={catalog.questions}
      session={session}
      sectorName={sectorName(catalog.sectors, props.sectorId)}
      languageName={props.languageName}
      notices={collectorNotice}
      answerPanel={props.answerPanel}
      isSubmitting={props.isSubmitting}
      canSubmit={props.canSubmit}
      blocked={props.blocked}
      recording={props.recording}
      onSubmit={props.onSubmit}
      onPause={props.onPause}
      onChangeSector={props.onChangeSector}
    />
  );
}
